import { AlertCircle, ChevronDown, Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// ============================================
// TEXT INPUT COMPONENT
// ============================================
export const TextInput = ({
  label,
  value,
  onChange,
  placeholder = "",
  type = "text",
  error = "",
  disabled = false,
  required = false,
  icon = null,
  min = undefined,
  max = undefined,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputType = type === "password" && showPassword ? "text" : type;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">
            {icon}
          </div>
        )}
        <input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          className={`w-full h-10 px-3 ${
            icon ? "pl-10" : ""
          } py-2.5 rounded border text-base transition-all
            ${
              error
                ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                : "border-[#d8dae5] focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
            }
            ${
              disabled
                ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                : "bg-white text-[#191d23]"
            }
            placeholder:text-slate-400 focus:outline-none`}
        />
        {type === "password" && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#191d23] transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
};
// ============================================
// NUMBER INPUT COMPONENT
// ============================================
export const NumberInput = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999999,
  step = 1,
  placeholder = "0",
  error = "",
  required = false,
}) => {
  const increment = () => {
    const newValue = Math.min((parseFloat(value) || 0) + step, max);
    onChange(newValue);
  };

  const decrement = () => {
    const newValue = Math.max((parseFloat(value) || 0) - step, min);
    onChange(newValue);
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative flex">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          className={`flex-1 h-10 px-3 py-2.5 rounded-l border text-base
            ${
              error
                ? "border-red-500 focus:border-red-500"
                : "border-[#d8dae5] focus:border-emerald-700"
            }
            focus:outline-none focus:ring-2 focus:ring-emerald-100 transition-all`}
        />
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
};
// ============================================
// DATE AND TIME SELECTION INPUT COMPONENT
// ============================================
export const DateTimeSection = ({
  dateRange,
  timeRange,
  onDateChange,
  onTimeChange,
}) => {
  return (
    <div className="flex flex-col gap-3 p-4 rounded border border-[#e3eae2]">
      <div className="flex items-center justify-between gap-3">
        {/* From DateTime */}
        <DateTimePicker
          date={dateRange.start}
          time={timeRange.start}
          onDateChange={(date) => onDateChange({ ...dateRange, start: date })}
          onTimeChange={(time) => onTimeChange({ ...timeRange, start: time })}
        />

        {/* Arrow Button */}
        <button className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#86C540] hover:bg-[#75b330] rounded-full transition-colors">
          <ChevronDown size={20} className="text-white rotate-[-90deg]" />
        </button>

        {/* To DateTime */}
        <DateTimePicker
          date={dateRange.end}
          time={timeRange.end}
          onDateChange={(date) => onDateChange({ ...dateRange, end: date })}
          onTimeChange={(time) => onTimeChange({ ...timeRange, end: time })}
        />
      </div>
    </div>
  );
};

// Individual DateTime Picker Component
export const DateTimePicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
  variant = "simple", // "picker" or "simple"
  mode = "both", // "date", "time", or "both"
  error = "", // Error message
  required = false,
  minDate = null, // Minimum selectable date (YYYY-MM-DD)
  maxDate = null, // Maximum selectable date (YYYY-MM-DD)
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const datePickerRef = useRef(null);
  const timePickerRef = useRef(null);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
      if (
        timePickerRef.current &&
        !timePickerRef.current.contains(event.target)
      ) {
        setShowTimePicker(false);
      }
    };
    if (showDatePicker || showTimePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker, showTimePicker]);

  const formatDate = (dateStr) => {
    if (!dateStr) return { dayName: "", monthDay: "" };
    const d = new Date(dateStr + "T00:00:00Z");
    return {
      dayName: d.toLocaleDateString("en-US", {
        weekday: "long",
        timeZone: "UTC",
      }),
      monthDay: d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }),
    };
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hour = h % 12 || 12;
        const ampm = h < 12 ? "AM" : "PM";
        const min = m === 0 ? "00" : m;
        times.push(`${hour}:${min} ${ampm}`);
      }
    }
    return times;
  };

  // const handleDateSelect = (e) => {
  //   const selectedDate = e.target.value;

  //   // Validate the selected date
  //   if (isDateAllowed(selectedDate)) {
  //     onDateChange(selectedDate);
  //     setShowDatePicker(false);
  //   } else {
  //     // Show feedback that date is not allowed
  //     alert("This date is not available for selection");
  //   }
  // };

  const { dayName, monthDay } = formatDate(date);

  // SIMPLE VARIANT
  if (variant === "simple") {
    return (
      <div className="flex flex-col gap-2">
        {(mode === "date" || mode === "both") && (
          <TextInput
            label="Date"
            value={date}
            onChange={onDateChange}
            required
            type="date"
            min={minDate}
            max={maxDate}
          />

        )}
        {(mode === "time" || mode === "both") && (
          <TextInput
            label="Time"
            type="time"
            value={time}
            onChange={onTimeChange}
            required
          />
        )}
      </div>
    );
  }

  // PICKER VARIANT
  return (
    <div className="flex flex-col items-center gap-1 flex-1 relative">
      {/* Date Section */}
      {(mode === "date" || mode === "both") && (
        <>
          <p className={`text-xs ${error ? "text-red-500" : "text-[#64748B]"}`}>
            {dayName || "Select date"}
            {mode === "both" && ","}
          </p>
          <button
            type="button"
            onClick={() => setShowDatePicker(!showDatePicker)}
            className={`text-2xl font-semibold transition-colors ${
              error
                ? "text-red-600 hover:text-red-700"
                : "text-[#191d23] hover:text-emerald-700"
            }`}
          >
            {monthDay || "Mar 19"}
          </button>

          {showDatePicker && (
            <div
              ref={datePickerRef}
              className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-max"
            >
              <input
                type="date"
                value={date}
                // onChange={handleDateSelect}
                // {...getDateInputProps()}
                className={`px-3 py-2 border rounded focus:outline-none w-full ${
                  error
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-300 focus:border-emerald-700"
                }`}
              />
            </div>
          )}
        </>
      )}

      {/* Time Section */}
      {(mode === "time" || mode === "both") && (
        <>
          <button
            type="button"
            onClick={() => setShowTimePicker(!showTimePicker)}
            className={`${
              mode === "time" ? "text-2xl font-semibold" : "text-sm"
            } transition-colors ${
              error
                ? "text-red-600 hover:text-red-700"
                : "text-[#64748B] hover:text-emerald-700"
            }`}
          >
            {time || "9:00 AM"}
          </button>

          {showTimePicker && (
            <div
              ref={timePickerRef}
              className="absolute top-full mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              {generateTimeOptions().map((timeOption) => (
                <button
                  type="button"
                  key={timeOption}
                  onClick={() => {
                    onTimeChange(timeOption);
                    setShowTimePicker(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-[#191d23] hover:bg-emerald-50 transition-colors whitespace-nowrap"
                >
                  {timeOption}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1 mt-2">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================
// SELECT DROPDOWN COMPONENT
// ============================================
export const Select = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select option",
  error = "",
  required = false,
  disabled = false,
  isLoading = false,
  loadingText = "Loading...",
  emptyText = "No options available",
  renderOption = null, // Custom function to render option label
  optionValue = "value", // Key for option value
  optionLabel = "label", // Key for option label
}) => {
  // Helper function to get option value
  const getOptionValue = (option) => {
    
    if (typeof option === "string" || typeof option === "number") {
      return option;
    }
    return option[optionValue];
  };

  // Helper function to get option label
  const getOptionLabel = (option) => {
    if (typeof option === "string" || typeof option === "number") {
      return option;
    }

    // Use custom render function if provided
    if (renderOption) {
      return renderOption(option);
    }

    return option[optionLabel];
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => {onChange(e.target.value)}}
          disabled={disabled || isLoading}
          className={`w-full h-10 px-3 pr-10 py-2 rounded border text-base appearance-none transition-all
            ${
              error
                ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                : "border-[#d8dae5] focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
            }
            ${
              disabled || isLoading
                ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                : "bg-white text-[#191d23]"
            }
            focus:outline-none cursor-pointer`}
        >
          <option value="">{placeholder}</option>

          {isLoading ? (
            <option disabled>{loadingText}</option>
          ) : options.length === 0 ? (
            <option disabled>{emptyText}</option>
          ) : (
            options.map((option, index) => (
              <option
                key={getOptionValue(option) || index}
                value={getOptionValue(option)}
                disabled={option.disabled}
              >
                {getOptionLabel(option)}
              </option>
            ))
          )}
        </select>

        {isLoading ? (
          <Loader2
            size={20}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none animate-spin"
          />
        ) : (
          <ChevronDown
            size={20}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none"
          />
        )}
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================
// RADIO BUTTON GROUP COMPONENT
// ============================================
export const RadioGroup = ({
  label,
  options = [],
  selected,
  onChange,
  direction = "vertical",
  required = false,
  disabled = false, // Disable entire group
}) => {
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label
          className={`text-sm font-medium ${
            disabled ? "text-gray-400" : "text-[#191d23]"
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div
        className={`flex ${
          direction === "horizontal" ? "flex-row gap-4" : "flex-col gap-2"
        }`}
      >
        {options.map((option) => {
          // Check if this specific option or the entire group is disabled
          const isDisabled = disabled || option.disabled;

          return (
            <label
              key={option.value}
              className={`flex items-center gap-2 ${
                isDisabled
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer group"
              }`}
            >
              <div className="relative w-5 h-5">
                <input
                  type="radio"
                  value={option.value}
                  checked={selected === option.value}
                  onChange={(e) => !isDisabled && onChange(e.target.value)}
                  disabled={isDisabled}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-full border-2 transition-all
                  ${
                    isDisabled
                      ? "border-gray-300 bg-gray-100"
                      : selected === option.value
                      ? "border-emerald-700 bg-white"
                      : "border-[#d8dae5] bg-white group-hover:border-emerald-500"
                  }`}
                >
                  {selected === option.value && (
                    <div
                      className={`absolute inset-0 m-auto w-3 h-3 rounded-full ${
                        isDisabled ? "bg-gray-400" : "bg-emerald-700"
                      }`}
                    />
                  )}
                </div>
              </div>
              <span
                className={`text-base ${
                  isDisabled ? "text-gray-400" : "text-[#191d23]"
                }`}
              >
                {option.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};
